/** @jsxImportSource @emotion/react */
import Plot from "react-plotly.js";
import { Alert, Badge, FormGroup, Input, Label, Table } from "reactstrap";
import { isPhaseEnabled, phaseHeading } from "../../../../constants/ripeningPhaseSlots";
import { formatProgramFieldValue, PROGRAM_FIELDS } from "../../../../constants/ripeningProgramFields";
import {
    isStorageDefinition,
    STORE_LOW_OXYGEN,
    storeInjectsN2,
    storeValuesOf,
    withDerivedStoreEthylene,
} from "../../../../constants/ripeningStorePrograms";
import { RipeningProgram } from "../../../../hooks/useRipeningPrograms";
import StoreSettingsFields from "../Helpers/StoreSettingsFields";

interface RipeningProgramPreviewProps {
    program: RipeningProgram;
    editable?: boolean;
    onChange?: (program: RipeningProgram) => void;
}

const chartFields = [
    { key: "T_End", label: PROGRAM_FIELDS.T_End.label, color: "#6610f2", group: "temperature" },
    { key: "Spv_C2H4", label: PROGRAM_FIELDS.Spv_C2H4.label, color: "#d63384", group: "c2h4" },
] as const;

// * Client-facing: labels only, never the ripening room field names.
const previewFields = [
    PROGRAM_FIELDS.TimeSpan,
    PROGRAM_FIELDS.T_Start,
    PROGRAM_FIELDS.T_End,
    PROGRAM_FIELDS.Spv_O2,
    PROGRAM_FIELDS.Spv_CO2,
    PROGRAM_FIELDS.Modus_C2H4,
    PROGRAM_FIELDS.Spv_C2H4,
    PROGRAM_FIELDS.Modus_Aereation,
].map(({ key, label }) => ({ key, label }));
// * Only these can be changed when starting a cycle (the start request accepts no other overrides).
const EDITABLE_KEYS = new Set<string>(["T_End", "Spv_C2H4"]);

const formatDuration = (minutes: number) => {
    const hours = minutes / 60;
    return Number.isInteger(hours) ? `${hours} hours` : `${minutes} minutes`;
};

export default function RipeningProgramPreview({ program, editable = false, onChange }: RipeningProgramPreviewProps) {
    const phases = [...(program.definition.ripewise?.phases ?? [])].sort((a, b) => a.phase - b.phase);
    const isStorage = isStorageDefinition(program.definition);
    const isRipewise = program.definition.mode === "ripewise" && !isStorage;
    const store = storeValuesOf(program.definition);
    // * Only the storage temperature can change at the start, and only on a storage program in store form.
    const setStoreSetpoint = editable && onChange && program.definition.mode === "ripewise"
        ? (_key: string, value: number | undefined) => {
            const next = { ...store };
            if (value === undefined) delete next.Store_Setpoint;
            else next.Store_Setpoint = value;
            onChange({ ...program, definition: { ...program.definition, ripewise: { ...program.definition.ripewise, store: withDerivedStoreEthylene(next) } } });
        }
        : undefined;
    const updatePhaseValue = (phaseNumber: number, key: string, value: string) => {
        if (!onChange) return;
        const numericValue = value === "" ? undefined : Number(value);
        const nextPhases = phases.map((phase) => ({ ...phase, values: { ...phase.values } }));
        const phaseIndex = nextPhases.findIndex((phase) => phase.phase === phaseNumber);
        if (phaseIndex < 0) return;
        if (numericValue === undefined) delete nextPhases[phaseIndex].values[key];
        else nextPhases[phaseIndex].values[key] = numericValue;
        if (key === "T_End" && phaseIndex + 1 < nextPhases.length) {
            if (numericValue === undefined) delete nextPhases[phaseIndex + 1].values.T_Start;
            else nextPhases[phaseIndex + 1].values.T_Start = numericValue;
        }
        onChange({
            ...program,
            definition: {
                ...program.definition,
                ripewise: { ...program.definition.ripewise, phases: nextPhases },
            },
        });
    };

    const series = chartFields.flatMap(({ key, label, color, group }) => {
        let elapsedHours = 0;
        const points: { x: number; y: number }[] = [];
        // * The planned run: a disabled phase only runs when it is switched on during the cycle.
        phases.filter(isPhaseEnabled).forEach((phase) => {
            const duration = Number(phase.values.TimeSpan);
            const value = Number(phase.values[key]);
            if (!Number.isFinite(duration) || duration <= 0) return;
            if (Number.isFinite(value)) {
                points.push({ x: elapsedHours, y: value }, { x: elapsedHours + duration, y: value });
            }
            elapsedHours += duration;
        });
        return points.length > 0 ? [{
            x: points.map((point) => point.x),
            y: points.map((point) => point.y),
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: label,
            line: { color, shape: "hv" as const },
            yaxis: group === "c2h4" ? "y2" : "y",
        }] : [];
    });

    return (
        <div data-testid="ripening-program-preview">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <h5 className="mb-1">{program.name}</h5>
                    <span className="text-muted">{program.fruit_type} · {isStorage ? "storage until stopped" : formatDuration(program.duration_minutes)}</span>
                </div>
                <span className="text-muted small">Program preview</span>
            </div>
            {isStorage && (
                <div data-testid="preview-storage">
                    {storeInjectsN2(store) && (
                        <Alert color="warning" className="py-2 px-3 mb-2 small" data-testid="preview-low-oxygen">
                            {"⚠ "}<strong>Low oxygen:</strong> {STORE_LOW_OXYGEN}
                        </Alert>
                    )}
                    <StoreSettingsFields
                        values={store}
                        onChange={setStoreSetpoint}
                        editable={(key) => key === "Store_Setpoint"}
                        idPrefix="preview-store"
                    />
                </div>
            )}
            {isRipewise && (
                <>
                    <div className="table-responsive">
                        <Table bordered size="sm">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    {phases.map((phase) => (
                                        <th key={phase.phase} data-testid={`preview-phase-${phase.phase}`}>
                                            {phaseHeading(phase.phase).title}
                                            {!isPhaseEnabled(phase) && <Badge color="secondary" className="ms-1">Disabled</Badge>}
                                            {phaseHeading(phase.phase).subtitle && <div className="small text-muted fw-normal">{phaseHeading(phase.phase).subtitle}</div>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewFields.map(({ key, label }) => (
                                    <tr key={key}>
                                        <th>{label}</th>
                                        {phases.map((phase, phaseIndex) => (
                                            <td key={phase.phase} style={{ minWidth: 108 }}>
                                                {(() => {
                                                    const displayValue = key === "T_Start" && phaseIndex === 0 ? "-" : formatProgramFieldValue(key, phase.values[key]);
                                                    if (!editable || !EDITABLE_KEYS.has(key)) return displayValue;
                                                    return (
                                                        <FormGroup className="mb-0">
                                                            <Label className="visually-hidden" for={`preview-${key}-${phase.phase}`}>{label} phase {phase.phase}</Label>
                                                            <Input id={`preview-${key}-${phase.phase}`} bsSize="sm" type="number" min={key === "Spv_C2H4" ? 0 : undefined} step="any" value={phase.values[key] ?? ""} onChange={(event) => updatePhaseValue(phase.phase, key, event.target.value)} />
                                                        </FormGroup>
                                                    );
                                                })()}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <Plot
                        data={series}
                        layout={{ autosize: true, height: 240, margin: { l: 20, r: 20, t: 12, b: 40 }, xaxis: { title: { text: "Hours" }, rangemode: "tozero" }, yaxis: { visible: false, rangemode: "tozero" }, yaxis2: { visible: false, overlaying: "y", rangemode: "tozero" }, legend: { orientation: "h" } }}
                        config={{ responsive: true, displayModeBar: false }}
                        style={{ width: "100%" }}
                        useResizeHandler
                    />
                </>
            )}
            {!isRipewise && !isStorage && (
                <div className="text-muted">This {program.definition.mode} program has no phase-level preview.</div>
            )}
        </div>
    );
}
